import styled from 'styled-components';
import { NavbarText } from 'reactstrap';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import { useRef, useState } from 'react';
import { format } from 'date-fns';
import {
  blueCharcoal_2,
  paradigmLightBlue,
  pickledBluewood_2,
} from '@paradigm/design-system/src/colors';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { formatOrdinal } from '@paradigm/utils/formatDecimal';
import { useVolumesListState } from '@paradigm/features/src/leaderboard/contexts/volumes-list';

export default function MyFirm() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { volumes, error, userFirm } = useVolumesListState();

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  if (userFirm == null || error != null) {
    return null;
  }

  const deskLabels = userFirm.desks.map((desk: string) => (
    <span key={desk} data-testid={desk}>
      {desk}
    </span>
  ));

  const firmIndex = volumes.findIndex(
    (result) => result.firmName === userFirm.name,
  );

  const rank = firmIndex === -1 ? NEGATIVE_SIGN : formatOrdinal(firmIndex + 1);
  const rankTitle = `${format(new Date(), 'MMMM')} Rank`;

  return (
    <Container>
      <MonthlyRank>
        <RankTitle>{rankTitle}</RankTitle>
        <RankNum>{rank}</RankNum>
      </MonthlyRank>
      <FirmContainer ref={containerRef}>
        <FirmName
          data-testid="leaderboard-firm-name"
          role="button"
          onClick={handleMouseEnter}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {userFirm.name}
        </FirmName>
        <StyledPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          container={containerRef.current}
          placement="bottom-start"
          transition
        >
          {({ TransitionProps }) =>
            deskLabels.length > 0 && (
              <Fade {...TransitionProps} timeout={350}>
                <DeskLabels>{deskLabels}</DeskLabels>
              </Fade>
            )
          }
        </StyledPopper>
      </FirmContainer>
    </Container>
  );
}

const MonthlyRank = styled.div`
  height: 30px;
  display: flex;
  align-items: center;
  background-color: black;
  border-radius: 7px 0 0 7px;
  padding: 0 1rem;
  border-right: 1px solid ${pickledBluewood_2};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const RankTitle = styled.span`
  font-size: 0.75rem;
  line-height: 1rem;
  text-transform: capitalize;
  display: block;
  opacity: 0.7;
  padding-top: 0.25rem;
`;
const RankNum = styled.span`
  font-size: 1rem;
  line-height: 1rem;
  text-transform: capitalize;
  display: block;
  font-weight: 700;
  color: ${paradigmLightBlue};
  padding-left: 0.5rem;
`;

const FirmName = styled(NavbarText)`
  background-color: black;
  border-radius: 0px 7px 7px 0px;
  text-align: center;
  font-size: 0.875rem;
  line-height: 1rem;
  max-width: 200px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  text-transform: capitalize;
  cursor: pointer;
  display: block;
  padding: 0.5rem 1rem;
  height: 30px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-radius: 7px;
  }
`;

const FirmContainer = styled.div`
  max-width: 200px;
  position: relative;
  box-sizing: border-box;
`;

const DeskLabels = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  background-color: ${blueCharcoal_2};
  box-shadow: 0 0 20px 0 rgba(74, 28, 186, 0.3);
  padding: 10px 12px;
  gap: 8px;
  max-height: 500px;
  overflow: auto;

  span {
    opacity: 0.7;
  }
`;

const StyledPopper = styled(Popper)`
  width: auto;
  padding-top: 0.25rem;
  width: 100%;
  z-index: 15;
`;

const Container = styled.div`
  position: relative;
  display: flex;
`;
